/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { css } from 'aphrodite';
import { useDispatch } from 'react-redux';

import { colors, LoadingSpinner } from '@blend/consumer-components';

import { AxiosError } from 'axios';
import { VideoProvider, useVideoContext } from '../../contexts/videoProvider/videoContext';
import { useRoom } from '../../hooks/useRoom';
import { useRootSelector } from '../../hooks/useRootSelector';
import { setSelectedDocument, setDocumentEditUrl } from '../../actions/documentActions';
import { LOCAL_TRACKS_LENGTH } from '../../contexts/videoProvider/hooks/useLocalTracks';
import { NotaryAPI } from '../../api/notary/types';
import { notaryAPI } from '../../api/notary';
import { setCallDetails } from '../../actions/callActions';
import { addNotification } from '../../actions/pageActions';
import { startCall as _startCall, startVideoRecording as _startVideoRecording } from '../../utils/call';
import { usePreFetchVideoProvider } from '../../hooks/videoProvider/usePreFetchVideoProvider';
import { styles } from './styles';
import { usePollNotaryCallDetails } from './hooks/usePollNotaryCallDetails';
import { useCallEndRedirect } from './hooks/useCallEndRedirect';
import { useDocuments } from './hooks/useDocuments';
import { usePollSigningAction } from './hooks/usePollSigningAction';
import { usePollUsers } from './hooks/usePollUsers';
import { SigningRoomNavigation } from './SigningRoomNavigation/SigningRoomNavigation';
import { SigningRoomPageContent } from './SigningRoomPageContent/SigningRoomPageContent';
import { SigningRoomNavigationItemIds } from './SigningRoomNavigation/signingRoomItems';
import PendingTaskBanner from './PendingTaskBanner';
import { getSelectedDocumentPdfUrl, getSelectedRoomDocumentPdfUrl } from '@utils/documents';
import { getVideoVendorApiKey } from '@utils/videoVendorStorageUtils';
import { NotificationItem } from '@components/common/Notifications/NotificationItem';
import { ConsentModal } from '@components/NotaryRoom/ConsentModal/ConsentModal';
import { ExitSessionConfirmationModal } from '@components/NotaryRoom/ExitSessionConfirmationModal/ExitSessionConfirmationModal';
import { DocumentsPendingTaggingModal } from '@components/NotaryRoom/DocumentsPendingTaggingModal/DocumentsPendingTaggingModal';
import SigningDocument from '@components/common/Documents/SigningDocument';
import { DocumentStatus } from '@shared/constants/document';
import { setRumSessionUserName } from '@utils/datadogRum';
import { ActiveCallStatuses } from '@shared/constants/call';

export namespace NotarySigningRoomPage {
  export interface OwnProps {}
  export type Props = OwnProps;
}

const isReviewingDocumentAndSelectingDifferentDocument = (documentId: string, documents: SigningDocument[]) => {
  // Are we not reviewing exactly one document?
  const documentsInReview = documents.filter(doc => doc.status === DocumentStatus.PENDING_REVIEW);
  const isReviewingExactlyOneDocument = documentsInReview.length === 1;
  if (!isReviewingExactlyOneDocument) {
    return false;
  }
  // Are we selecting a different document than the one in review?
  const isSelectingDocumentInReview = documentId === documentsInReview[0].id;
  return !isSelectingDocumentInReview;
};

export const _NotarySigningRoomPage: React.FC<NotarySigningRoomPage.Props> = ({}) => {
  const { documentsPendingTaggingNames } = usePollNotaryCallDetails();
  useCallEndRedirect();
  useDocuments();
  usePollSigningAction();
  usePollUsers();

  const dispatch = useDispatch();
  const { callDetails, document, signingRoom, layout, user } = useRootSelector(reduxState => reduxState);
  const { documents } = document;
  const { selectedNavigationItemId } = signingRoom;
  const { isMobileView } = layout;
  const { roomId } = useRoom();
  const { connect: connectToVideoProvider, localTracks, videoProvider } = useVideoContext();
  const [apiKey, setAPIKey] = useState(getVideoVendorApiKey(roomId));
  const [roomParticipants, setRoomParticipants] = useState<NotaryAPI.RoomParticipantsResponse | null>(null);
  const [consentModalIsOpen, setConsentModalIsOpen] = useState<boolean>(false);
  const [isConsentModalSubmitting, setIsConsentModalSubmitting] = React.useState(false);
  const [taggedDocumentsModalIsOpen, setTaggedDocumentsModalIsOpen] = useState<boolean>(false);
  const callDetailsEl = useRef<NotaryAPI.CallDetailsResponse | null>(null);
  const callId = callDetails?.id;

  setRumSessionUserName(roomId, 'notary-signing', user?.id);

  const startCall = async () => {
    if (callId === undefined) {
      throw new Error('no Call available to start');
    }
    const call = await _startCall(callId, dispatch);
    setCallDetails(call)(dispatch);
    callDetailsEl.current = call;
  };

  const startVideoRecording = async () => {
    if (callId === undefined) {
      throw new Error('cannot start video recording: no Call available to start');
    }
    const call = await _startVideoRecording(callId);
    setAPIKey(call.notaryVideoAccessToken!);
    setCallDetails(call)(dispatch);
    callDetailsEl.current = call;
  };

  const setSelectedDocumentAndFetchPdf = useCallback(
    async (documentId: string) => {
      if (isReviewingDocumentAndSelectingDifferentDocument(documentId, documents)) {
        const notification: NotificationItem.PropsWithoutId = {
          type: 'error',
          iconType: 'error',
          content: 'Finish reviewing the current document before selecting another document.',
          shouldAutoRemove: true,
        };
        addNotification(notification)(dispatch);
      } else {
        let fileUrl;
        if (callDetails?.status && !ActiveCallStatuses.includes(callDetails.status)) {
          fileUrl = await getSelectedRoomDocumentPdfUrl(documentId, roomId, documents);
        } else {
          fileUrl = await getSelectedDocumentPdfUrl(documentId, callDetails, documents, true);
        }
        setDocumentEditUrl(undefined)(dispatch);
        setSelectedDocument({
          id: documentId,
          pdfUrl: fileUrl,
        })(dispatch);
      }
    },
    [dispatch, callDetails, documents],
  );

  const setSelectedDocumentAndFetchEditUrl = useCallback(
    async (documentId: string) => {
      if (callId !== undefined) {
        const { url } = await notaryAPI.documents.fetchCallDocumentEditUrl(callId, documentId);
        setDocumentEditUrl(url)(dispatch);
        setSelectedDocument({
          id: documentId,
          pdfUrl: undefined,
        })(dispatch);
      }
    },
    [dispatch, callId],
  );

  const finalizeDocumentAndSetActive = useCallback(
    async (documentId: string) => {
      if (callId !== undefined) {
        await notaryAPI.documents.finalizeCallDocument(callId, documentId);
        await setSelectedDocumentAndFetchPdf(documentId);
      }
    },
    [callId, setSelectedDocumentAndFetchPdf],
  );

  const ParticipantStrip = useMemo(() => {
    return videoProvider.getParticipantStripComponent();
  }, [videoProvider]);

  useEffect(() => {
    const fetchCallDetails = async () => {
      let call;
      try {
        call = await notaryAPI.rooms.fetchCallDetailsForRoom(roomId);
      } catch (e) {
        const error = e as AxiosError<{ message?: string }>;
        const status = error.response?.status;
        // Show error when there is an appointment in progress or journal entry is not blocked.
        // Notary access is already blocked at pipeline. The following alert error is for notary who manually input
        // the signing room url on browser
        if (status === 400) {
          const msg = error.response?.data?.message || '';
          alert(msg);
        }
        throw error;
      }

      if (call.notaryVideoAccessToken) {
        setAPIKey(call.notaryVideoAccessToken);
      }
      setCallDetails(call)(dispatch);
      callDetailsEl.current = call;
      setConsentModalIsOpen(!call.notaryEConsentedAt);
    };

    fetchCallDetails();
  }, [dispatch, roomId]);

  useEffect(() => {
    async function fetchRoomParticipants() {
      const participants = await notaryAPI.rooms.fetchParticipantsInRoom(roomId);
      // TODO: error-handling if there are no participants in the room
      setRoomParticipants(participants);
    }
    fetchRoomParticipants();
  }, [roomId]);

  useEffect(() => {
    // checking for local track length to make sure getting local video/audio track
    // has finished before connecting to the room
    if (apiKey && localTracks.length === LOCAL_TRACKS_LENGTH) {
      connectToVideoProvider(apiKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, localTracks.length]);

  const isPausingRef = useRef(false);

  // Automatically pause signing when documents are pending tagging
  useEffect(() => {
    let isCancelled = false;
    const shouldPause = documentsPendingTaggingNames.length > 0 && callDetails?.status === 'SIGNING';

    if (shouldPause && callId && !isPausingRef.current) {
      const pauseSigningAndFetchDetails = async () => {
        isPausingRef.current = true;
        try {
          await notaryAPI.calls.pauseSigning(callId);
          const updatedCall = await notaryAPI.calls.fetchCall(callId);
          if (!isCancelled) {
            setCallDetails(updatedCall)(dispatch);
            setTaggedDocumentsModalIsOpen(true);
          }
        } finally {
          isPausingRef.current = false;
        }
      };
      pauseSigningAndFetchDetails();
    } else if (documentsPendingTaggingNames.length === 0) {
      setTaggedDocumentsModalIsOpen(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [documentsPendingTaggingNames, callDetails?.status, callId, dispatch]);

  const showLoading = !callDetails || (consentModalIsOpen && !roomParticipants);
  if (showLoading) {
    return (
      <div className={css(styles.pageLoadingSpinnerHolder)}>
        <LoadingSpinner size="lg" color={colors.catalyst} />
      </div>
    );
  }

  const isMobileParticipantsView =
    isMobileView && selectedNavigationItemId === SigningRoomNavigationItemIds.participants;

  return (
    <div className={css(styles.signingRoomPageWrapper)}>
      <div className={css(styles.wrapper)} data-it="NotarySigningRoomPage">
        <PendingTaskBanner roomId={roomId} isNotarySide={true} />
        {!isMobileParticipantsView && apiKey && <ParticipantStrip />}
        <div className={css(styles.main)}>
          {callDetails && (
            <SigningRoomNavigation
              handleDocumentClick={setSelectedDocumentAndFetchPdf}
              handleEditDocumentClick={setSelectedDocumentAndFetchEditUrl}
              handleFinalizeDocumentClick={finalizeDocumentAndSetActive}
              startCall={startCall}
              startVideoRecording={startVideoRecording}
            />
          )}
          {callDetails && <SigningRoomPageContent setSelectedDocumentAndFetchPdf={setSelectedDocumentAndFetchPdf} />}
          {roomParticipants && callId !== undefined && (
            <ConsentModal
              contentLabel="notary_consent_modal"
              callId={callId}
              isOpen={consentModalIsOpen}
              onSubmitSuccess={() => setConsentModalIsOpen(false)}
              isSubmitting={isConsentModalSubmitting}
              setIsSubmitting={setIsConsentModalSubmitting}
              participants={roomParticipants}
            />
          )}
        </div>
        {callDetails && <ExitSessionConfirmationModal roomId={roomId} callId={callId} />}
        {!isMobileView && (
          <DocumentsPendingTaggingModal
            isOpen={taggedDocumentsModalIsOpen}
            documentsPendingTaggingNames={documentsPendingTaggingNames}
            roomId={roomId}
            handleClose={() => setTaggedDocumentsModalIsOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export const NotarySigningRoomPage = (props: NotarySigningRoomPage.OwnProps) => {
  const { isLoading, videoProviderName } = usePreFetchVideoProvider(true);
  return !isLoading ? (
    <VideoProvider opts={{ dominantSpeaker: true }} videoProviderName={videoProviderName}>
      <_NotarySigningRoomPage {...props} />
    </VideoProvider>
  ) : null;
};
